import type { Goal } from '@medplum/fhirtypes';

import { ELUVE_FHIR_NAMESPACE } from './constants';

type GoalTarget = NonNullable<Goal['target']>[number];

/**
 * The body site a goal's measure target was picked at (valueString, the picked
 * Observation's `bodySite` text). `Goal.target.measure` carries only a coding,
 * so the left and right readings of one measure resolve to the same target and
 * collide. Absent on a target written before ENG-5790 and on one picked from a
 * site-less reading — both of which is what keeps them matching by coding alone.
 */
export const GOAL_TARGET_BODY_SITE_EXTENSION_URL = `${ELUVE_FHIR_NAMESPACE}/StructureDefinition/goal-target-body-site`;

export const goalTargetBodySite = (target: GoalTarget): string | null =>
  target.extension?.find(
    (extension) => extension.url === GOAL_TARGET_BODY_SITE_EXTENSION_URL,
  )?.valueString ?? null;

export const withGoalTargetBodySite = (
  target: GoalTarget,
  bodySite: string | null | undefined,
): GoalTarget => {
  // Identity-preserving when nothing changes: callers re-stamp an entry they
  // just read, and `nextGoalTargets` tells the re-picked entry from its
  // siblings by reference.
  if ((bodySite || null) === goalTargetBodySite(target)) return target;
  const others = (target.extension ?? []).filter(
    (extension) => extension.url !== GOAL_TARGET_BODY_SITE_EXTENSION_URL,
  );
  const extension = bodySite
    ? [
        ...others,
        { url: GOAL_TARGET_BODY_SITE_EXTENSION_URL, valueString: bodySite },
      ]
    : others;
  if (extension.length === 0) {
    const cleared = { ...target };
    delete cleared.extension;
    return cleared;
  }
  return { ...target, extension };
};

/** Trim + lowercase, the same normalization a measure row's series key uses, so
 *  "Right shoulder" and "right shoulder" name one site. */
export const normalizeBodySite = (
  bodySite: string | null | undefined,
): string => (bodySite ?? '').trim().toLowerCase();

/** Whether a goal target describes a reading taken at `bodySite`. A target that
 *  records no site matches any, which is the coding-only tiebreak every target
 *  had before ENG-5790. */
export const bodySiteMatchesTarget = (
  targetBodySite: string | null | undefined,
  bodySite: string | null | undefined,
): boolean =>
  !targetBodySite ||
  normalizeBodySite(targetBodySite) === normalizeBodySite(bodySite);

/** The lateralities a reading can be taken at. `none` is the midline or
 *  non-sided case, which composes to the site alone. */
export type BodySide = 'none' | 'left' | 'right' | 'bilateral';

export const BODY_SIDES: readonly BodySide[] = [
  'none',
  'left',
  'right',
  'bilateral',
];

/**
 * Laterality + site as one body-site text. The single composer for it: every
 * writer, extraction and clinician alike, has to spell a site the same way or
 * the readings fork into separate trend series (`normalizeBodySite` is the only
 * forgiveness there is). The prefix is skipped when the side is already spelled
 * inside the site, so a model that wrote "left shoulder" never becomes "left
 * left shoulder".
 */
export const sidedBodySiteText = (
  side: string | null | undefined,
  bodySite: string | null | undefined,
): string | null => {
  const site = bodySite?.trim() || null;
  const laterality = side && side !== 'none' ? side : null;
  if (!site) return laterality;
  // Whole-word match, so "left" embedded in e.g. "cleft palate" still gets the prefix.
  if (!laterality || new RegExp(`\\b${laterality}\\b`, 'i').test(site)) {
    return site;
  }
  return `${laterality} ${site}`;
};

/** A site text with any leading laterality word removed, so an established
 *  "right shoulder" reading suggests "shoulder" for a new one. */
export const deLateralizedSite = (
  bodySite: string | null | undefined,
): string => (bodySite ?? '').trim().replace(/^(left|right|bilateral)\s+/i, '');
